import React from 'react'
import { Grid, Typography, Checkbox, Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Suggest from '../../Apartment/Stepper/Steps/Suggest'
import DigivakuusFaqLink from '../../../components/Digivakuus/DigivakuusFaqLink'
import { Trans } from 'react-i18next'

const useStyles = makeStyles((theme) => ({
    modalTitle: {
        fontWeight: 700,
        marginTop: 8,
        marginBottom: theme.spacing(2)
    },
    acceptBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '25px 18px !important',
        width: '100%',
        background: '#F7F8F9',
        color: '#2F2F2F',
        borderRadius: 20,
        marginTop: 6,
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            padding: '16px',
            maxWidth: '100%'
        }
    },
    acceptRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 16,
        width: '100%',
        '& + &': {
            marginTop: theme.spacing(3)
        }
    },
    checkboxRoot: {
        width: 30,
        height: 30,
        padding: 0,
        margin: 0,
        color: '#94004A',
        '&$checkboxChecked': {
            color: '#94004A'
        }
    },
    checkboxChecked: {},
    acceptTexts: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        color: '#000'
    },
    acceptHeader: {
        fontWeight: 700,
        fontSize: "18px",
        marginTop: "2px",
        lineHeight: '24px'
    },
    acceptText: {
        marginTop: 8,
        fontWeight: 400,
        fontSize: 18,
        lineHeight: 1.5 as any,
        color: '#2F2F2F',
        whiteSpace: 'pre-line'
    },
    cashbackBox: {
        marginTop: theme.spacing(2),
        marginLeft: '-5px',
        padding: '12px 16px',
        borderRadius: 16,
        backgroundColor: '#FFFFFF',
        border: '1px solid #94004A'
    },
    cashbackTitle: {
        fontWeight: 700,
        fontSize: 16,
        color: '#94004A'
    },
    cashbackText: {
        marginTop: 4,
        fontSize: 16,
        lineHeight: 1.4 as any,
        color: '#5C5C5C'
    },
    faqInlineWrapper: {
        display: 'flex',
        width: '100%',
        marginTop: theme.spacing(1)
    },
    faqButton: {
        padding: 0,
        marginTop: 10,
        fontSize: 17,
        textTransform: 'none' as const,
        textDecoration: 'underline',
        fontWeight: 600,
        color: '#94004A',
        '&:hover': {
            textDecoration: 'underline'
        }
    },
    navRow: {
        marginTop: 6,
        '&.MuiGrid-spacing-xs-2 > .MuiGrid-item': {
            padding: '0 8px'
        }
    }
}))

const ContractGeneratorDigivakuusStep = ({
    t,
    classes: parentClasses,
    digivakuusDepositAccepted,
    setDigivakuusDepositAccepted,
    digivakuusEscrowServiceAccepted,
    setDigivakuusEscrowServiceAccepted,
    ropoRentChargingServiceAccepted,
    setRopoRentChargingServiceAccepted,
    navigateTo,
    ChevronLeftIcon,
    ChevronRightIcon,
    showExistingProducts,
    applicantIsDigivakuusVerified = false
}) => {
    const classes = useStyles()
    const checkboxContentKey = applicantIsDigivakuusVerified
        ? 'apartments.rented.depositServiceCheckboxContent'
        : 'apartments.rented.unverifiedDepositServiceCheckboxContent'

    return (
        <Grid container className={parentClasses?.formContainer}>
            {/* IMPORTANT: make this an item, and use the parent's width class */}
            <Grid xs={12} className={parentClasses?.responsiveWidth}>
                <Grid container justify="center" style={{ margin: 'auto' }}>
                    <Typography variant="h4" align="center" className={parentClasses?.heading}>
                        {t('apartments.rented.depositServiceTitle')}
                    </Typography>

                    <Grid item sm={12} style={{ marginTop: '-16px' }}>
                        <Suggest
                            isModal
                            containerMaxWidth="100%"
                            contentMaxWidth="100%"
                            content={t('apartments.rented.depositServiceInfo')}
                        />
                    </Grid>

                    {showExistingProducts && (
                        <>
                            <div className={classes.acceptBox}>
                                <div className={classes.acceptRow}>
                                    <Checkbox
                                        classes={{ root: classes.checkboxRoot, checked: classes.checkboxChecked }}
                                        checked={digivakuusDepositAccepted}
                                        onChange={(e) => setDigivakuusDepositAccepted(e.target.checked)}
                                    />
                                    <div className={classes.acceptTexts}>
                                        <Typography className={classes.acceptHeader}>
                                            {t('apartments.rented.depositServiceCheckboxHeader')}
                                        </Typography>
                                        <Typography className={classes.acceptText}>
                                            {t(checkboxContentKey)}
                                        </Typography>
                                        <div className={classes.faqInlineWrapper}>
                                            <DigivakuusFaqLink
                                                t={t}
                                                variant='landlord'
                                                buttonClassName={classes.faqButton}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={classes.acceptBox}>
                                <div className={classes.acceptRow}>
                                    <Checkbox
                                        classes={{ root: classes.checkboxRoot, checked: classes.checkboxChecked }}
                                        checked={digivakuusEscrowServiceAccepted}
                                        onChange={(e) => setDigivakuusEscrowServiceAccepted(e.target.checked)}
                                    />
                                    <div className={classes.acceptTexts}>
                                        <Typography className={classes.acceptHeader}>
                                            {t('apartments.rented.digivakuusEscrowServiceCheckboxHeader')}
                                        </Typography>
                                        <Typography className={classes.acceptText}>
                                            {t('apartments.rented.digivakuusEscrowServiceCheckboxContent')}
                                        </Typography>
                                        <div className={classes.faqInlineWrapper}>
                                            <DigivakuusFaqLink
                                                t={t}
                                                variant='rentInsurance'
                                                buttonClassName={classes.faqButton}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <div className={classes.acceptBox}>
                        <div className={classes.acceptRow}>
                            <Checkbox
                                classes={{ root: classes.checkboxRoot, checked: classes.checkboxChecked }}
                                checked={ropoRentChargingServiceAccepted}
                                onChange={(e) => setRopoRentChargingServiceAccepted(e.target.checked)}
                            />
                            <div className={classes.acceptTexts}>
                                <Typography className={classes.acceptHeader}>
                                    {t('apartments.rented.ropoRentChargingTitle')}
                                </Typography>
                                <Typography className={classes.acceptText}>
                                    <Trans
                                        t={t}
                                        i18nKey='apartments.rented.ropoRentChargingInfo'
                                        components={{ strong: <strong /> }}
                                    />
                                </Typography>
                                <div className={classes.cashbackBox}>
                                    <Typography className={classes.cashbackTitle}>
                                        {t('apartments.rented.ropoRentChargingCashbackTitle')}
                                    </Typography>
                                    <Typography className={classes.cashbackText}>
                                        <Trans
                                            t={t}
                                            i18nKey='apartments.rented.ropoRentChargingCashbackInfo'
                                            components={{ strong: <strong /> }}
                                        />
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Grid container spacing={2} className={classes.navRow}>
                        <Grid item xs={12} sm={6}>
                            <Grid item>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    className={`${parentClasses?.responsiveButton || ''} ${parentClasses?.bottomButton1 || ''} ${parentClasses?.bottomButton || ''}`}
                                    onClick={() => navigateTo(2)}
                                >
                                    <Grid container justify="center" alignItems="center">
                                        <Grid item>{t('contractCreator.stepper.cancel')}</Grid>
                                        <Grid item className={`${parentClasses?.leftFloatingIcon || ''} ${parentClasses?.arrowIcon || ''}`}>
                                            {ChevronLeftIcon ? <ChevronLeftIcon color="#94004A" /> : null}
                                        </Grid>
                                    </Grid>
                                </Button>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Grid item>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    className={`${parentClasses?.responsiveButton || ''} ${parentClasses?.bottomButton2 || ''} ${parentClasses?.bottomButton || ''}`}
                                    onClick={() => navigateTo(4)}
                                >
                                    <Grid container justify="center" alignItems="center">
                                        <Grid item>{t('contractCreator.stepper.next')}</Grid>
                                        <Grid item className={`${parentClasses?.rightFloatingIcon || ''} ${parentClasses?.arrowIcon || ''}`}>
                                            {ChevronRightIcon ? <ChevronRightIcon color="#D8DCDF" /> : null}
                                        </Grid>
                                    </Grid>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default ContractGeneratorDigivakuusStep
